<template>
    <div class="reginsp">
        <!-- 조회 조건 -->
        <div class="card mb-3">
            <div class="card-body">
                <div class="row align-items-end">
                    <div class="col-md-3">
                        <b-form-group label="조회 시작일" label-for="reginsp-start" class="mb-2">
                            <b-form-input id="reginsp-start" v-model="startDate" type="date"></b-form-input>
                        </b-form-group>
                    </div>
                    <div class="col-md-3">
                        <b-form-group label="조회 종료일" label-for="reginsp-end" class="mb-2">
                            <b-form-input id="reginsp-end" v-model="endDate" type="date"></b-form-input>
                        </b-form-group>
                    </div>
                    <div class="col-md-2">
                        <b-form-group label="검수 상태" label-for="reginsp-state" class="mb-2">
                            <b-form-select id="reginsp-state" v-model="state" :options="stateOptions"></b-form-select>
                        </b-form-group>
                    </div>
                    <div class="col-md-2">
                        <b-form-group label="상품명" label-for="reginsp-name" class="mb-2">
                            <b-form-input id="reginsp-name" v-model="productName" placeholder="상품명 일부"
                                @keyup.enter="search"></b-form-input>
                        </b-form-group>
                    </div>
                    <div class="col-md-2 mb-2">
                        <b-button variant="primary" class="w-100" :disabled="loading" @click="search">
                            {{ loading ? '조회 중...' : '조회' }}
                        </b-button>
                    </div>
                </div>
                <small class="form-text text-muted mt-2">
                    아직 검수가 끝나지 않은 상품만 나옵니다. 검수를 통과해 판매가 시작된 상품은 이 목록에서 빠집니다.
                </small>
            </div>
        </div>

        <!-- 요약 -->
        <div v-if="!loading && !errorMessage && searched" class="reginsp__summary mb-3">
            <span class="badge badge-secondary mr-2">전체 {{ total }}건</span>
            <span v-for="(count, label) in byState" :key="label" class="badge badge-light border mr-2">
                {{ label }} {{ count }}건
            </span>
        </div>

        <!-- 실패: 0건과 구분해서 보여준다 -->
        <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

        <div v-if="loading" class="text-center p-4 text-muted">쿠팡에서 등록 심사 상태를 불러오는 중입니다.</div>

        <div v-else-if="!errorMessage && searched && items.length === 0" class="text-center p-4 text-muted">
            이 기간에 검수가 진행 중이거나 반려된 상품이 없습니다.
        </div>

        <!-- 목록 -->
        <div v-else-if="items.length" class="table-responsive">
            <table class="table table-sm table-hover reginsp__table">
                <thead class="thead-light">
                    <tr>
                        <th style="width:60px;"></th>
                        <th>상품명</th>
                        <th style="width:110px;">등록일</th>
                        <th style="width:120px;">검수 상태</th>
                        <th style="width:150px;">막힌 단계</th>
                        <th>사유</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in items" :key="rowKey(item, index)">
                        <td>
                            <img v-if="thumbUrl(item)" :src="thumbUrl(item)" class="reginsp__thumb" alt=""
                                @error="onThumbError">
                        </td>
                        <td>
                            <div>{{ item.productName }}</div>
                            <small class="text-muted">{{ item.barcode || item.skuId || '' }}</small>
                        </td>
                        <td>{{ formatDate(item.registerDate) }}</td>
                        <td>
                            <span class="badge" :class="stateClass(item.state)">{{ stateLabel(item.state) }}</span>
                        </td>
                        <td>
                            <span v-if="blockedAt(item)">
                                {{ stepLabel(blockedAt(item).step) }}
                                <small class="text-muted d-block">{{ progressLabel(blockedAt(item).progress) }}</small>
                            </span>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                            <div v-for="(reason, i) in reasonsOf(item)" :key="i" class="reginsp__reason">
                                {{ reason }}
                            </div>
                            <span v-if="!reasonsOf(item).length" class="text-muted">-</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 쪽 넘기기 -->
        <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
            <b-pagination v-model="page" :total-rows="total" :per-page="sizePerPage" :disabled="loading"
                @change="onPageChange"></b-pagination>
        </div>
    </div>
</template>

<script>
/**
 * RegistrationInspection.vue — 로켓배송 상품 등록 심사 상태
 *
 * 화면은 브라우저라 supplier.coupang.com 을 직접 못 부른다. 쿠플러스 확장이 대신 부른다.
 * 보내는 쪽 `GET_REGISTRATION_INSPECTION` ↔ 받는 쪽 `REGISTRATION_INSPECTION_RESPONSE` —
 * 키 이름은 couplus-extension/script.js·background.js 와 **한 글자도 달라선 안 된다.**
 *
 * ⚠ 이 목록은 "아직 안 끝난 것"만 보여준다. 통과한 상품은 빠지므로 여기서 등록 성공률을 내면 안 된다.
 */

// 쿠팡은 반려 사유를 rejectStatus·rejectRemark 가 아니라 steps[].tooltips[].code 에 넣는다.
// 모르는 코드는 "알 수 없는 사유"로 뭉개지 않고 코드를 그대로 보여준다 — 뭉개면 사용자가
// 쿠팡 화면을 다시 열어야 하고, 그러면 이 탭을 만든 이유가 없어진다.
// (같은 표가 couplus-cli/tools/rocket.tools.js 에도 있다. 확장에 두지 않은 이유는
//  확장은 문구 한 줄을 고치려 해도 웹스토어 심사를 거쳐야 해서다.)
//
// ⚠ **코드 이름을 보고 뜻을 짐작하면 틀린다.** CATEGORY_REQUIRES_REVIEW 는 이름과 달리 카테고리가
//    아니라 **공급가** 반려다 — 2026-09-16 에 쿠팡 화면(가격/정책 · "쿠팡 기준 미달")에서 직접 읽었다.
//    아래 두 줄은 쿠팡이 화면에 쓰는 문장 그대로다.
const TOOLTIP_CODES = {
    TOOLTIP_ONBOARDED_COMPLETED: '상품 등록이 접수되었습니다.',
    TOOLTIP_HOTW_IN_PROGRESS: '상품이 쿠팡 매입정책에 부합하는지 검수중입니다.',
    CATEGORY_REQUIRES_REVIEW: '등록하신 공급가는 쿠팡의 기준/정책에 부합하지 않아 반려되었습니다. 공급가를 다시 제안하거나 담당 BM에게 문의해주세요.',
    // 아래 둘은 쿠팡이 화면에 아무 문장도 띄우지 않는다. 가격·정책 단계에서 반려됐다는 사실만 확실하니
    // 원인을 단정하지 않는 선에서 적었다.
    AUTO_REJECTION_MANAGED_LIST: '쿠팡 정책에 따라 자동 반려되었습니다. 자세한 사유는 쿠팡 판매자센터에서 확인해주세요.',
    LOW_PPM: '마진이 쿠팡 기준에 못 미쳐 반려되었습니다. 공급가를 다시 제안해주세요.',
};

const STATE_LABELS = {
    INSPECTION: '상품 검수중',
    INSPECTION_COMPLETE: '상품 검수 완료',
    REJECTION: '상품 검수 반려',
};

const STEP_LABELS = {
    ONBOARDED: '상품 등록 접수',
    HOTW: '가격·정책 검토',
    R21: '상품 정보 검토',
    PO: '발주서 발행',
};

const PROGRESS_LABELS = {
    COMPLETED: '완료',
    IN_PROGRESS: '진행중',
    REJECTED: '반려',
};

export default {
    name: 'RegistrationInspection',
    props: {
        suppliercode: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            startDate: '',
            endDate: '',
            state: '',
            productName: '',
            page: 1,
            sizePerPage: 50,

            loading: false,
            searched: false,
            // 확장이 아예 대답하지 않는 경우(미설치·구버전)를 잡는다. 없으면 회전자가 영원히 돈다 —
            // 사용자는 그걸 "조회 결과가 없다"로 읽는다.
            responseTimer: null,
            errorMessage: '',
            items: [],
            total: 0,
            totalPages: 0,

            stateOptions: [
                { value: '', text: '전체' },
                { value: 'INSPECTION', text: '상품 검수중' },
                { value: 'INSPECTION_COMPLETE', text: '상품 검수 완료' },
                { value: 'REJECTION', text: '상품 검수 반려' },
            ],
        };
    },
    computed: {
        byState() {
            const out = {};
            for (const item of this.items) {
                const label = this.stateLabel(item.state) || '(상태 없음)';
                out[label] = (out[label] || 0) + 1;
            }
            return out;
        },
    },
    created() {
        const today = new Date();
        const from = new Date(today.getTime() - 120 * 24 * 60 * 60 * 1000);
        this.endDate = this.toInputDate(today);
        this.startDate = this.toInputDate(from);
    },
    mounted() {
        window.addEventListener('message', this.handleExtensionMessage);
    },
    beforeDestroy() {
        window.removeEventListener('message', this.handleExtensionMessage);
        this.clearResponseTimer();
    },
    methods: {
        clearResponseTimer() {
            if (this.responseTimer) {
                clearTimeout(this.responseTimer);
                this.responseTimer = null;
            }
        },

        toInputDate(date) {
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${date.getFullYear()}-${m}-${d}`;
        },

        formatDate(value) {
            if (!value) return '-';
            try {
                const date = new Date(value);
                if (isNaN(date.getTime())) return String(value);
                return this.toInputDate(date);
            } catch (e) {
                console.error('등록일 표시 오류:', e);
                return String(value);
            }
        },

        /**
         * productImageURL 은 이름과 달리 **URL 여러 개를 쉼표로 이어 붙인 문자열**이다
         * (2026-09-16 화면 실측: 한 건에 6개가 들어 있었다). 그대로 src 에 넣으면 전부 깨진다.
         */
        thumbUrl(item) {
            const raw = item && item.productImageURL ? String(item.productImageURL) : '';
            const first = raw.split(',')[0].trim();
            return /^https?:\/\//.test(first) ? first : '';
        },

        // 쿠팡 이미지가 사라진 건도 있다. 깨진 아이콘을 보여주느니 자리를 비운다.
        onThumbError(event) {
            if (event && event.target) event.target.style.display = 'none';
        },

        rowKey(item, index) {
            return item.skuId || item.uniqueItemId || item.quotationId || `row-${index}`;
        },

        stateLabel(value) {
            return STATE_LABELS[value] || value || '';
        },

        stateClass(value) {
            if (value === 'REJECTION') return 'badge-danger';
            if (value === 'INSPECTION_COMPLETE') return 'badge-success';
            return 'badge-warning';
        },

        stepLabel(value) {
            return STEP_LABELS[value] || value || '';
        },

        progressLabel(value) {
            return PROGRESS_LABELS[value] || value || '';
        },

        /**
         * "어디서 막혔나" 한 줄. 반려된 단계가 있으면 그것, 없으면 진행 중인 단계.
         * 단계 네 개를 다 그리면 표가 넓어져서 안 읽힌다 — 갈린 자리 하나만 보여준다.
         */
        blockedAt(item) {
            const steps = Array.isArray(item.steps) ? item.steps : [];
            return (
                steps.find(s => s && s.progress === 'REJECTED') ||
                steps.find(s => s && s.progress === 'IN_PROGRESS') ||
                null
            );
        },

        reasonsOf(item) {
            const step = this.blockedAt(item);
            const tooltips = step && Array.isArray(step.tooltips) ? step.tooltips : [];
            return tooltips
                .map(t => (t && t.code ? TOOLTIP_CODES[t.code] || t.code : ''))
                .filter(Boolean);
        },

        toEpochMs(dateString, endOfDay) {
            const parts = String(dateString || '').split('-');
            if (parts.length !== 3) return null;
            const date = new Date(
                Number(parts[0]),
                Number(parts[1]) - 1,
                Number(parts[2]),
                endOfDay ? 23 : 0,
                endOfDay ? 59 : 0,
                endOfDay ? 59 : 0,
                endOfDay ? 999 : 0,
            );
            return isNaN(date.getTime()) ? null : date.getTime();
        },

        onPageChange(nextPage) {
            this.page = nextPage;
            this.$nextTick(() => this.request());
        },

        search() {
            this.page = 1;
            this.request();
        },

        request() {
            const start = this.toEpochMs(this.startDate, false);
            const end = this.toEpochMs(this.endDate, true);
            if (start === null || end === null) {
                this.errorMessage = '조회 기간을 다시 확인해주세요.';
                return;
            }
            if (start > end) {
                this.errorMessage = '조회 시작일이 종료일보다 뒤입니다. 기간을 다시 확인해주세요.';
                return;
            }
            if (!this.suppliercode) {
                this.errorMessage = '판매자코드가 없습니다. 쿠팡 판매자센터 연동을 먼저 확인해주세요.';
                return;
            }

            this.loading = true;
            this.errorMessage = '';

            this.clearResponseTimer();
            this.responseTimer = setTimeout(() => {
                if (!this.loading) return;
                this.loading = false;
                this.searched = true;
                this.items = [];
                this.total = 0;
                this.totalPages = 0;
                this.errorMessage = '쿠플러스 확장 프로그램이 응답하지 않습니다. 확장 프로그램이 설치·활성화되어 있는지 확인한 뒤 다시 시도해주세요.';
                console.error('등록 심사 상태 조회: 확장 프로그램 응답 없음 (60초 초과)');
            }, 60000);

            window.postMessage({
                type: 'GET_REGISTRATION_INSPECTION',
                data: {
                    suppliercode: this.suppliercode,
                    startDate: start,
                    endDate: end,
                    state: this.state,
                    progress: '',
                    productName: this.productName,
                    page: this.page,
                    sizePerPage: this.sizePerPage,
                },
            }, '*');
        },

        handleExtensionMessage(event) {
            if (!event.data || typeof event.data !== 'object') return;

            // 확장이 supplier 탭을 못 찾거나 판매자코드가 안 맞으면 이 모양으로 온다.
            if (event.data.msg === 'CoupangRegAlert' && this.loading) {
                this.clearResponseTimer();
                this.loading = false;
                this.searched = true;
                this.errorMessage = event.data.res || '쿠팡 판매자센터 연결을 확인해주세요.';
                return;
            }

            if (event.data.type !== 'REGISTRATION_INSPECTION_RESPONSE') return;

            this.clearResponseTimer();
            this.loading = false;
            this.searched = true;

            // **0건과 실패를 구분한다.** 실패를 빈 목록으로 받으면 "심사 대기 없음"으로 읽힌다.
            if (event.data.success !== true) {
                this.items = [];
                this.total = 0;
                this.totalPages = 0;
                this.errorMessage = event.data.error || '등록 심사 상태를 가져오지 못했습니다.';
                console.error('등록 심사 상태 조회 실패:', event.data.error);
                return;
            }

            this.errorMessage = '';
            this.items = Array.isArray(event.data.items) ? event.data.items : [];
            const nav = event.data.pageNavigator || {};
            this.total = typeof nav.totalElements === 'number' ? nav.totalElements : this.items.length;
            this.totalPages = typeof nav.totalPages === 'number' ? nav.totalPages : 1;
        },
    },
};
</script>

<style scoped lang="scss">
.reginsp {
    &__thumb {
        width: 44px;
        height: 44px;
        object-fit: cover;
        border-radius: 4px;
    }

    &__table {
        font-size: 0.875rem;

        td {
            vertical-align: middle;
        }
    }

    &__reason {
        line-height: 1.4;

        & + & {
            margin-top: 0.25rem;
        }
    }

    &__summary {
        font-size: 0.9rem;
    }
}
</style>
